import React, { useRef, useState, useCallback, useEffect } from "react";
import { Loader2, Upload, RotateCcw, Trash2, MousePointerClick } from "lucide-react";
import { resolveAsset } from "../lib/api";

const PRESET_ASPECTS = [
  { label: "16:9", value: 16 / 9 },
  { label: "4:3", value: 4 / 3 },
  { label: "1:1", value: 1 },
  { label: "3:4", value: 3 / 4 },
  { label: "21:9", value: 21 / 9 },
];

const DEFAULT_ASPECT = 16 / 9;

/**
 * CoverImageEditor — interactive preview that lets the admin:
 *   1. Drag the bottom-right corner to set the public aspect ratio (saved as decimal)
 *   2. Click anywhere on the image to set the focal point (saved as % x/y)
 *   3. Pick a preset aspect (16:9, 4:3, 1:1, 3:4, 21:9)
 *   4. Reset crop to default (16:9 + centre focal)
 *
 * Props:
 *   coverImage    string | null    URL of the cover (already uploaded)
 *   aspect        number | null    saved cover_aspect (decimal); null = default 16/9
 *   focalX        number | null    0-100 percent (null = 50)
 *   focalY        number | null    0-100 percent (null = 50)
 *   onChange      ({ aspect, focalX, focalY }) => void   fired on every commit
 *   onUpload      () => void                              triggers file picker
 *   onClear       () => void                              clears the cover
 *   uploading     bool
 */
export default function CoverImageEditor({
  coverImage,
  aspect,
  focalX,
  focalY,
  onChange,
  onUpload,
  onClear,
  uploading,
}) {
  const wrapperRef = useRef(null);
  const [draftAspect, setDraftAspect] = useState(null);

  const effectiveAspect = draftAspect ?? aspect ?? DEFAULT_ASPECT;
  const fx = focalX ?? 50;
  const fy = focalY ?? 50;

  const startDrag = useCallback(
    (e) => {
      if (!coverImage) return;
      e.preventDefault();
      e.stopPropagation();
      const rect = wrapperRef.current?.getBoundingClientRect();
      if (!rect) return;
      const startX = e.clientX;
      const startY = e.clientY;
      const startW = rect.width;
      const startH = rect.height;

      const onMove = (ev) => {
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;
        // Bottom-right handle: dragging right grows width, dragging down grows height.
        const newW = Math.max(60, startW + dx);
        const newH = Math.max(60, startH + dy);
        const ratio = Math.min(4, Math.max(0.3, newW / newH));
        setDraftAspect(ratio);
      };
      const onUp = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        setDraftAspect((r) => {
          if (r != null) onChange({ aspect: Number(r.toFixed(3)) });
          return null;
        });
      };
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [coverImage, onChange],
  );

  const onClickFocal = useCallback(
    (e) => {
      if (!coverImage) return;
      // Only accept clicks directly on the image area, not on the resize handle.
      if (e.target?.dataset?.role === "resize-handle") return;
      const rect = wrapperRef.current?.getBoundingClientRect();
      if (!rect) return;
      const px = ((e.clientX - rect.left) / rect.width) * 100;
      const py = ((e.clientY - rect.top) / rect.height) * 100;
      const clampedX = Math.min(100, Math.max(0, Math.round(px)));
      const clampedY = Math.min(100, Math.max(0, Math.round(py)));
      onChange({ focalX: clampedX, focalY: clampedY });
    },
    [coverImage, onChange],
  );

  // Keyboard shortcut: Escape clears any in-flight draft drag.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && draftAspect != null) setDraftAspect(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [draftAspect]);

  const reset = () =>
    onChange({ aspect: null, focalX: null, focalY: null });

  if (!coverImage) {
    return (
      <button
        type="button"
        onClick={onUpload}
        data-testid="cover-upload"
        className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-black/15 bg-[#F8FAFC] px-4 py-10 font-display text-sm font-semibold text-[var(--brand-body)] hover:border-[#0A0F17] hover:text-[var(--brand-ink)]"
      >
        {uploading ? (
          <>
            <Loader2 size={14} className="animate-spin" /> Uploading…
          </>
        ) : (
          <>
            <Upload size={14} /> Upload a cover image
          </>
        )}
      </button>
    );
  }

  return (
    <div className="rounded-md border border-black/10 bg-white p-3" data-testid="cover-image-editor">
      <div
        ref={wrapperRef}
        onClick={onClickFocal}
        className="relative w-full cursor-crosshair overflow-hidden rounded-md bg-[#F8FAFC] select-none"
        style={{
          aspectRatio: effectiveAspect,
          maxWidth: "100%",
        }}
        data-testid="cover-preview-wrapper"
      >
        <img
          src={resolveAsset(coverImage)}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          style={{ objectPosition: `${fx}% ${fy}%` }}
          draggable={false}
        />

        {/* Focal-point dot */}
        <span
          className="pointer-events-none absolute h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-[#6CABDD] shadow-lg"
          style={{ left: `${fx}%`, top: `${fy}%` }}
          aria-hidden
          data-testid="cover-focal-dot"
        />

        {/* Aspect badge */}
        <span className="pointer-events-none absolute left-2 top-2 rounded-sm bg-[#0A0F17]/85 px-2 py-1 font-display text-xs font-bold text-white">
          {effectiveAspect.toFixed(2)}:1
        </span>

        {/* Hint */}
        <span className="pointer-events-none absolute right-2 top-2 inline-flex items-center gap-1 rounded-sm bg-[#0A0F17]/85 px-2 py-1 text-xs text-white">
          <MousePointerClick size={11} /> click to focus
        </span>

        {/* Bottom-right resize handle */}
        <span
          data-role="resize-handle"
          data-testid="cover-resize-handle"
          onMouseDown={startDrag}
          className="absolute bottom-0 right-0 h-6 w-6 cursor-nwse-resize rounded-tl-md bg-[#0A0F17] text-white"
          title="Drag to change aspect ratio"
        >
          <span
            className="pointer-events-none absolute right-1 bottom-1 h-3 w-3 border-b-2 border-r-2 border-white"
            aria-hidden
          />
        </span>
      </div>

      {/* Controls row */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="label-kicker text-[var(--brand-body)] mr-1">Aspect</span>
        {PRESET_ASPECTS.map((p) => (
          <button
            key={p.label}
            type="button"
            onClick={() => onChange({ aspect: p.value })}
            data-testid={`cover-aspect-${p.label.replace(":", "x")}`}
            className={`rounded-sm border px-2 py-1 font-display text-xs font-bold ${
              Math.abs(effectiveAspect - p.value) < 0.01
                ? "border-[#0A0F17] bg-[#0A0F17] text-white"
                : "border-black/10 bg-white text-[var(--brand-ink)] hover:border-[#0A0F17]"
            }`}
          >
            {p.label}
          </button>
        ))}
        <span className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={reset}
            data-testid="cover-reset"
            className="inline-flex items-center gap-1 rounded-sm border border-black/10 px-2 py-1 label-kicker text-[var(--brand-body)] hover:border-[#0A0F17] hover:text-[var(--brand-ink)]"
            title="Reset crop and focal point"
          >
            <RotateCcw size={11} /> Reset
          </button>
          <button
            type="button"
            onClick={onUpload}
            disabled={uploading}
            data-testid="cover-replace"
            className="inline-flex items-center gap-1 rounded-sm border border-black/10 px-2 py-1 label-kicker text-[var(--brand-body)] hover:border-[#0A0F17] hover:text-[var(--brand-ink)]"
          >
            {uploading ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
            Replace
          </button>
          <button
            type="button"
            onClick={onClear}
            data-testid="cover-clear"
            className="inline-flex items-center gap-1 rounded-sm border border-[#E63946]/40 px-2 py-1 label-kicker text-[#E63946] hover:bg-[#E63946] hover:text-white"
          >
            <Trash2 size={11} /> Remove
          </button>
        </span>
      </div>
      <p className="mt-2 text-xs text-[var(--brand-body)]">
        Drag the bottom-right corner to set the aspect ratio. Click anywhere on the photo to choose where it crops on small screens.
      </p>
    </div>
  );
}
